import { useEffect, useState } from "react";
import { AuthGate } from "@/components/AuthGate";
import { ChatPanel } from "@/components/ChatPanel";
import { TenantPicker } from "@/components/TenantPicker";
import { whenOfficeReady, readHostTheme } from "@/lib/office";
import { signOut } from "@/lib/auth";
import { resolveTenant, clearTenantOverride } from "@/lib/tenant";
import { type TenantId } from "@/lib/tenants";
import { getSessionId } from "@/lib/session";
import { getBearer } from "@/lib/tokens";
import { initTelemetry, track, identify } from "@/lib/telemetry";

export function Taskpane() {
  const [officeReady, setOfficeReady] = useState(false);
  const [tenant, setTenant] = useState<TenantId | null>(() => resolveTenant());
  const [mirrorId, setMirrorId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    initTelemetry();

    whenOfficeReady().then(() => {
      if (cancelled) return;
      setOfficeReady(true);
      const theme = readHostTheme();
      if (theme === "light") {
        track("excel.host_theme_mismatch", { host_theme: "light" });
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Re-evaluate the cached bearer whenever office becomes ready OR the tenant
  // changes (Switch workspace path). Bearer storage is tenant-scoped, so a
  // returning user who already signed into this workspace skips the AuthGate.
  useEffect(() => {
    if (!officeReady) return;
    if (!tenant) {
      setMirrorId(null);
      return;
    }
    const token = getBearer();
    if (!token) {
      setMirrorId(null);
      return;
    }
    try {
      const decoded = atob(token);
      const m = decoded.split(":")[0];
      if (m) {
        setMirrorId(m);
        identify(m);
      }
    } catch {
      /* malformed token — treat as signed out */
    }
  }, [officeReady, tenant]);

  function handleSwitchWorkspace() {
    // Clear current-tenant credentials before flipping the tenant key, so
    // the right per-tenant keys get scrubbed.
    signOut(getSessionId());
    clearTenantOverride();
    setMirrorId(null);
    setTenant(null);
  }

  if (!officeReady) {
    return (
      <main className="flex h-screen items-center justify-center text-[var(--text-secondary)]">
        Loading myai…
      </main>
    );
  }

  if (!tenant) {
    return (
      <main className="h-screen">
        <TenantPicker onPicked={setTenant} />
      </main>
    );
  }

  if (!mirrorId) {
    return (
      <main className="h-screen">
        <AuthGate onSignedIn={setMirrorId} onSwitchWorkspace={handleSwitchWorkspace} />
      </main>
    );
  }

  return (
    <main className="h-screen">
      <ChatPanel
        mirrorId={mirrorId}
        onSignOut={() => setMirrorId(null)}
        onSwitchWorkspace={handleSwitchWorkspace}
      />
    </main>
  );
}
